import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth, adminApi, formatApiError } from "./AuthContext";

const ALLOWED = ["admin", "coordinator"];

export default function ShowConsole() {
  const { user, logout } = useAuth();
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [console_, setConsole] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  // ---------- access gating
  if (user === null)
    return <Centered>Loading…</Centered>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (!ALLOWED.includes(user.role))
    return (
      <Centered>
        <span data-testid="console-forbidden" style={{ color: "#ff6b6b" }}>
          The show console is admin / coordinator only.
        </span>
      </Centered>
    );

  return (
    <ConsoleInner
      user={user}
      logout={logout}
      eventId={eventId}
      navigate={navigate}
      events={events}
      setEvents={setEvents}
      console_={console_}
      setConsole={setConsole}
      err={err}
      setErr={setErr}
      loading={loading}
      setLoading={setLoading}
    />
  );
}

function ConsoleInner({
  user,
  logout,
  eventId,
  navigate,
  events,
  setEvents,
  console_,
  setConsole,
  err,
  setErr,
  loading,
  setLoading,
}) {
  // Load event list once for the selector
  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get("/events");
        setEvents(data.items || []);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, [setEvents, setErr]);

  // Auto-poll console snapshot
  const refresh = useCallback(async () => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await adminApi.get(`/events/${eventId}/console`);
      setConsole(data);
      setErr("");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId, setConsole, setErr, setLoading]);

  useEffect(() => {
    refresh();
    if (!eventId) return;
    const id = setInterval(refresh, 3000);
    return () => clearInterval(id);
  }, [refresh, eventId]);

  const openSpeaker = useMemo(
    () => console_?.speakers?.find((s) => s.id === console_?.open_speaker_id) || null,
    [console_]
  );

  const nextPending = useMemo(() => {
    if (!console_?.speakers) return null;
    return (
      console_.speakers
        .filter((s) => s.active && s.scoring_state === "pending")
        .sort((a, b) => a.performance_order - b.performance_order)[0] || null
    );
  }, [console_]);

  const doOpen = async (sp) => {
    try {
      await adminApi.post(`/events/${eventId}/speakers/${sp.id}/open`);
      refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const doClose = async (sp) => {
    if (
      !window.confirm(
        `Close scoring for "${sp.display_name}"? The audience will no longer be able to submit.`
      )
    )
      return;
    try {
      await adminApi.post(`/events/${eventId}/speakers/${sp.id}/close`);
      refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const doOpenNext = async () => {
    if (!nextPending) return;
    await doOpen(nextPending);
  };

  return (
    <div
      data-testid="show-console-page"
      style={{
        minHeight: "100vh",
        background: "#070D1F",
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
        padding: "28px 28px 56px 28px",
      }}
    >
      <Header user={user} logout={logout} />

      <EventPicker
        events={events}
        currentId={eventId}
        onSelect={(id) =>
          navigate(id ? `/admin/console/${id}` : "/admin/console")
        }
      />

      {!eventId && (
        <div
          data-testid="console-no-event-selected"
          style={{ opacity: 0.7, marginTop: 32, fontSize: 16 }}
        >
          Pick an event above to drive the show.
        </div>
      )}

      {err && (
        <div
          data-testid="console-error"
          style={{
            marginTop: 16,
            padding: "10px 14px",
            border: "1px solid #ff6b6b",
            color: "#ff6b6b",
          }}
        >
          {err}
        </div>
      )}

      {eventId && loading && <div style={{ marginTop: 24 }}>Loading…</div>}

      {eventId && console_ && (
        <>
          <NowOpenBanner
            speaker={openSpeaker}
            onClose={() => openSpeaker && doClose(openSpeaker)}
            checkinTotal={console_.checkin_total}
          />
          <PrimaryActions
            openSpeaker={openSpeaker}
            nextPending={nextPending}
            onOpenNext={doOpenNext}
          />
          <Lineup
            speakers={console_.speakers}
            openId={console_.open_speaker_id}
            onOpen={doOpen}
            onClose={doClose}
            checkinTotal={console_.checkin_total}
          />
        </>
      )}
    </div>
  );
}

function Header({ user, logout }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        borderBottom: "1px solid #14213D",
        paddingBottom: 14,
        marginBottom: 22,
      }}
    >
      <div>
        <div style={{ fontSize: 11, opacity: 0.6, letterSpacing: 3 }}>
          THE NEXT STAGE — SHOW CONSOLE
        </div>
        <div style={{ marginTop: 4, fontSize: 14, opacity: 0.85 }}>
          Operator: <strong>{user.name}</strong>{" "}
          <span
            style={{
              padding: "2px 8px",
              background: "#E5B25D",
              color: "#0A0F1F",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              marginLeft: 6,
            }}
          >
            {user.role.toUpperCase()}
          </span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <a
          href="/admin"
          style={{
            ...btnSecondary,
            textDecoration: "none",
            display: "inline-block",
            fontSize: 13,
          }}
        >
          ← Admin home
        </a>
        <button onClick={logout} style={btnSecondary}>
          Logout
        </button>
      </div>
    </div>
  );
}

function EventPicker({ events, currentId, onSelect }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label
        style={{ fontSize: 11, opacity: 0.6, letterSpacing: 2, marginRight: 12 }}
      >
        EVENT
      </label>
      <select
        data-testid="console-event-select"
        value={currentId || ""}
        onChange={(e) => onSelect(e.target.value)}
        style={{
          padding: "10px 12px",
          background: "#0A0F1F",
          color: "#fff",
          border: "1px solid #2a3556",
          fontSize: 14,
          minWidth: 340,
        }}
      >
        <option value="">— pick an event —</option>
        {events.map((e) => (
          <option key={e.id} value={e.id}>
            {e.name} ({e.status}) — {e.event_date}
          </option>
        ))}
      </select>
    </div>
  );
}

function NowOpenBanner({ speaker, onClose, checkinTotal }) {
  if (!speaker) {
    return (
      <div
        data-testid="console-nothing-open"
        style={{
          marginTop: 18,
          padding: "32px",
          border: "1px dashed #2a3556",
          textAlign: "center",
          opacity: 0.85,
          fontSize: 22,
          letterSpacing: 1,
        }}
      >
        Nothing open right now.
        <div style={{ fontSize: 12, opacity: 0.6, marginTop: 8, letterSpacing: 2 }}>
          USE "OPEN NEXT" OR PICK A SPEAKER BELOW TO START SCORING
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="console-now-open-banner"
      style={{
        marginTop: 18,
        padding: "30px 32px",
        background: "linear-gradient(135deg,#5dd6a4 0%,#3aa97a 100%)",
        color: "#04140A",
      }}
    >
      <div
        style={{ fontSize: 12, letterSpacing: 4, opacity: 0.7, marginBottom: 8 }}
      >
        OPEN NOW · SLOT #{speaker.performance_order + 1}
      </div>
      <div
        data-testid="console-now-open-name"
        style={{ fontSize: 40, fontWeight: 800, lineHeight: 1 }}
      >
        {speaker.display_name}
      </div>
      {speaker.talk_title && (
        <div style={{ fontSize: 16, marginTop: 6, opacity: 0.85 }}>
          {speaker.talk_title}
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginTop: 24,
        }}
      >
        <div
          data-testid="console-now-open-participation"
          style={{ fontSize: 22, fontWeight: 700 }}
        >
          {speaker.participation_count}{" "}
          {checkinTotal > 0 ? (
            <span style={{ opacity: 0.7, fontSize: 16, fontWeight: 500 }}>
              / {checkinTotal} checked-in
            </span>
          ) : null}{" "}
          <span style={{ fontSize: 11, letterSpacing: 2, opacity: 0.7 }}>
            SCORES IN
          </span>
        </div>
        <button
          data-testid="console-close-btn"
          onClick={onClose}
          style={{
            padding: "12px 22px",
            background: "#04140A",
            color: "#fff",
            border: 0,
            fontWeight: 700,
            fontSize: 15,
            cursor: "pointer",
            letterSpacing: 1,
          }}
        >
          CLOSE SCORING
        </button>
      </div>
    </div>
  );
}

function PrimaryActions({ openSpeaker, nextPending, onOpenNext }) {
  return (
    <div style={{ display: "flex", gap: 12, marginTop: 18, alignItems: "center" }}>
      <button
        data-testid="console-open-next-btn"
        onClick={onOpenNext}
        disabled={!nextPending}
        style={{
          ...btnPrimary,
          opacity: nextPending ? 1 : 0.4,
          cursor: nextPending ? "pointer" : "not-allowed",
        }}
      >
        {nextPending
          ? openSpeaker
            ? `▶ Close & open next: ${nextPending.display_name}`
            : `▶ Open next: ${nextPending.display_name}`
          : "▶ No pending speakers left"}
      </button>
    </div>
  );
}

function Lineup({ speakers, openId, onOpen, onClose, checkinTotal }) {
  return (
    <section style={{ marginTop: 28 }}>
      <h2 style={{ fontSize: 13, letterSpacing: 2, opacity: 0.7, margin: "0 0 12px 0" }}>
        LINEUP
      </h2>
      <ul
        data-testid="console-lineup"
        style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}
      >
        {speakers
          .filter((s) => s.active)
          .map((sp) => (
            <SpeakerRow
              key={sp.id}
              sp={sp}
              isOpen={sp.id === openId}
              onOpen={() => onOpen(sp)}
              onClose={() => onClose(sp)}
              checkinTotal={checkinTotal}
            />
          ))}
      </ul>
    </section>
  );
}

function SpeakerRow({ sp, isOpen, onOpen, onClose, checkinTotal }) {
  const state = sp.scoring_state;
  const labels = {
    pending: { text: "UPCOMING", color: "#9aa8c4" },
    open: { text: "OPEN NOW", color: "#5dd6a4" },
    closed: { text: "DONE", color: "#E5B25D" },
  };
  const lbl = labels[state] || labels.pending;
  return (
    <li
      data-testid={`console-row-${sp.id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "60px 1fr 220px 200px",
        gap: 16,
        alignItems: "center",
        padding: "14px 18px",
        background: isOpen ? "rgba(93,214,164,0.08)" : "#0F1A33",
        border: isOpen ? "2px solid #5dd6a4" : "1px solid #14213D",
      }}
    >
      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          textAlign: "center",
          opacity: 0.85,
        }}
      >
        {sp.performance_order + 1}
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>{sp.display_name}</div>
        {sp.talk_title && (
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{sp.talk_title}</div>
        )}
      </div>
      <div data-testid={`console-state-${sp.id}`} style={{ textAlign: "center" }}>
        <div
          style={{
            display: "inline-block",
            padding: "4px 12px",
            background: lbl.color,
            color: "#0A0F1F",
            fontWeight: 800,
            letterSpacing: 2,
            fontSize: 12,
          }}
        >
          {lbl.text}
        </div>
        <div
          data-testid={`console-count-${sp.id}`}
          style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}
        >
          {sp.participation_count}
          {checkinTotal > 0 ? ` / ${checkinTotal}` : ""} scored
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        {isOpen ? (
          <button
            data-testid={`console-row-close-${sp.id}`}
            onClick={onClose}
            style={{ ...btnSecondary, padding: "8px 14px", fontSize: 13 }}
          >
            CLOSE
          </button>
        ) : (
          <button
            data-testid={`console-row-open-${sp.id}`}
            onClick={onOpen}
            style={{
              ...btnPrimary,
              padding: "8px 14px",
              fontSize: 13,
              background: state === "pending" ? "#E5B25D" : "#3a4a6e",
              color: state === "pending" ? "#0A0F1F" : "#fff",
            }}
          >
            {state === "pending" ? "OPEN" : "RE-OPEN"}
          </button>
        )}
      </div>
    </li>
  );
}

function Centered({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#070D1F",
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

const btnPrimary = {
  padding: "12px 18px",
  background: "#E5B25D",
  color: "#0A0F1F",
  border: 0,
  fontWeight: 800,
  letterSpacing: 1,
  cursor: "pointer",
  fontSize: 14,
};
const btnSecondary = {
  padding: "10px 14px",
  background: "transparent",
  color: "#fff",
  border: "1px solid #2a3556",
  cursor: "pointer",
  fontSize: 13,
};
